/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { IntlProvider } from 'react-intl';
import { ThemeProvider } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import { AuthProvider, AuthReactConfig } from '@asgardeo/auth-react';
import useCreateChoreoTheme from 'theme/Theme';
import {
  apiBaseUrl,
  asgardeoConsoleUrl,
  asgardeoSdkConfig,
  asgardeoEUSpecificSdkConfig,
} from 'config';
import PageLoader from 'components/PreLoader/PageLoader';
import { UserProvider } from 'contexts/User';
import UserOrganization from 'types/userOrganization';
import { useState } from 'react';
import { SnackbarProvider } from 'notistack';
import { EnterpriseSubscriptionProvider } from 'contexts/EnterpriseSubscription';
import { SubscriptionProvider } from 'contexts/AsgardeoSubscription';
import { AsgardeoTier, CloudType } from 'data/api/interfaces';
import { Tier } from 'types/generated/Billing/models/tier';
import { AuthSessionVariable, getSessionItem } from 'utils/auth';
import { getCloudType } from 'utils/page-utils';
import { ResourceUsage } from 'types/generated/Billing/models/resource-usage';

interface Props {
  children: JSX.Element;
}

export default function Providers({ children }: Props) {
  const params = new URLSearchParams(window.location.search);
  const regionQueryParam = params.get('region') || '';
  const region = getSessionItem(AuthSessionVariable.REGION) || regionQueryParam;

  const reactAuthConfig: AuthReactConfig =
    region === 'EU' ? asgardeoEUSpecificSdkConfig : asgardeoSdkConfig;

  const sdkConfig: AuthReactConfig = {
    ...reactAuthConfig,
    resourceServerURLs: [
      'https://localhost:3000',
      'https://subscriptions.dv.wso2.com',
      apiBaseUrl,
    ],
    signOutRedirectURL:
      getCloudType() === CloudType.Asgardeo
        ? asgardeoConsoleUrl
        : reactAuthConfig.signOutRedirectURL,
  };

  const [organization, setOrganization] = useState<UserOrganization>(
    {} as UserOrganization
  );
  const [isEnterpriseUser, setIsEnterpriseUser] = useState(false);
  const [currentTier, setCurrentTier] = useState<Tier>({
    tierId: AsgardeoTier.FREE,
    name: AsgardeoTier.FREE,
  });

  const [organizationName, setOrganizationName] =
    // eslint-disable-next-line prettier/prettier
    useState<string | undefined>('');
  const [isOverviewDataFetching, setIsOverviewDataFetching] =
    useState<boolean>(true);
  const [monthlyUsage, setMonthlyUsage] = useState<
    Array<ResourceUsage> | undefined
  >([]);
  const [refetchOverviewData, setRefetchOverviewData] = useState<() => void>(
    () => () => {}
  );

  const theme = useCreateChoreoTheme();

  return (
    <AuthProvider config={sdkConfig} fallback={<PageLoader />}>
      <UserProvider value={{ organization, setOrganization }}>
        <EnterpriseSubscriptionProvider
          value={{ isEnterpriseUser, setIsEnterpriseUser }}
        >
          <SubscriptionProvider
            value={{
              organizationName,
              setOrganizationName,
              isOverviewDataFetching,
              setIsOverviewDataFetching,
              monthlyUsage,
              setMonthlyUsage,
              currentTier,
              setCurrentTier,
              refetchOverviewData,
              setRefetchOverviewData,
            }}
          >
            <IntlProvider locale="en" messages={{}}>
              <ThemeProvider theme={theme}>
                <SnackbarProvider
                  maxSnack={3}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  TransitionComponent={Collapse}
                >
                  {children}
                </SnackbarProvider>
              </ThemeProvider>
            </IntlProvider>
          </SubscriptionProvider>
        </EnterpriseSubscriptionProvider>
      </UserProvider>
    </AuthProvider>
  );
}
